import type { Icon } from '@phosphor-icons/react';
import { PlusIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import { cn } from './cn';

export interface NavItem {
  to: string;
  label: string;
  icon: Icon;
  end?: boolean;
  badge?: number;
}

/** Barra in basso sul telefono: due voci, il "+" centrale, due voci (spec 6.3). */
export function BottomBar({
  items,
  onPlus,
}: {
  items: [NavItem, NavItem, NavItem, NavItem];
  onPlus: () => void;
}) {
  const [a, b, c, d] = items;
  return (
    <nav
      aria-label="Navigazione principale"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-sheet pb-[env(safe-area-inset-bottom)] lg:hidden"
    >
      <div className="mx-auto grid h-16 max-w-xl grid-cols-5 items-stretch">
        <BarLink item={a} />
        <BarLink item={b} />
        <div className="flex items-start justify-center">
          <button
            type="button"
            onClick={onPlus}
            aria-label="Carica o crea"
            className="-mt-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-ink text-sheet shadow-[0_10px_24px_-8px_rgb(24_36_74/0.55)] ring-4 ring-paper transition-transform active:scale-95"
          >
            <PlusIcon size={26} weight="bold" aria-hidden />
          </button>
        </div>
        <BarLink item={c} />
        <BarLink item={d} />
      </div>
    </nav>
  );
}

function BarLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          'relative flex flex-col items-center justify-center gap-0.5 text-[0.6875rem] font-medium transition-colors',
          isActive ? 'text-ink' : 'text-ink-3',
        )
      }
    >
      {({ isActive }) => (
        <>
          <item.icon size={25} weight={isActive ? 'fill' : 'regular'} aria-hidden />
          <span>{item.label}</span>
          {item.badge ? (
            <span className="absolute top-2 left-[calc(50%+6px)] h-2.5 w-2.5 rounded-full bg-alert ring-2 ring-sheet" />
          ) : null}
        </>
      )}
    </NavLink>
  );
}

/** Barra laterale su desktop. */
export function Sidebar({
  brand,
  items,
  children,
  footer,
}: {
  brand: ReactNode;
  items: NavItem[];
  children?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <aside className="sticky top-0 hidden h-dvh w-[17rem] shrink-0 flex-col border-r border-line bg-sheet lg:flex">
      <div className="px-6 pt-7 pb-6">{brand}</div>
      <nav aria-label="Navigazione principale" className="flex flex-col gap-0.5 px-3">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex h-11 items-center gap-3 rounded-xl px-3 text-[0.9375rem] transition-colors',
                isActive
                  ? 'bg-ink/7 font-semibold text-ink'
                  : 'text-ink-2 hover:bg-ink/4 hover:text-ink',
              )
            }
          >
            {({ isActive }) => (
              <>
                <item.icon size={21} weight={isActive ? 'fill' : 'regular'} aria-hidden />
                <span className="flex-1">{item.label}</span>
                {item.badge ? (
                  <span className="tabular rounded-full bg-alert px-1.5 text-xs font-semibold text-white">
                    {item.badge}
                  </span>
                ) : null}
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="mt-6 min-h-0 flex-1 overflow-y-auto px-3">{children}</div>
      {footer ? <div className="border-t border-line-2 p-3">{footer}</div> : null}
    </aside>
  );
}
